import { useTranslation } from 'react-i18next';
import { ExternalLink, RefreshCw } from 'lucide-react';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatDate } from '@/lib/format';

import { CopyableValue } from './CopyableValue';
import { formatBaseAmount, formatBps, formatFiat, safeHttpUrl, truncateMiddle } from './format';
import type { PaymentStatus, PlatformPayment } from './types';

type Props = {
  payment: PlatformPayment;
  /** This row's re-sync is in flight: its button waits. */
  isResyncing: boolean;
  onResync: (cryptoOnchainPaymentId: string) => void;
};

const STATUS_VARIANT: Record<PaymentStatus, BadgeVariant> = {
  pending: 'secondary',
  detected: 'blue',
  paid: 'green',
  paid_late: 'teal',
  underpaid: 'yellow',
  overpaid: 'orange',
  expired: 'gray',
  failed: 'destructive',
};

/**
 * One payment of the staff list: what the shopper paid, what crypto-api settled
 * to the platform (or why it waived the share), a duplicate flag when the
 * order had already been paid by another payment, and the re-sync action.
 */
export function RecentPaymentRow({ payment, isResyncing, onResync }: Props) {
  const { t, i18n } = useTranslation('manageCryptoPayments');

  const amount = payment.amountFormatted ?? formatBaseAmount(payment.amountBase, payment.amountDecimals);
  const fiat = formatFiat(payment.fiatAmount, payment.fiatCurrency, i18n.language);
  const hasDistinctOriginal = !!payment.originalCurrency && payment.originalCurrency !== payment.fiatCurrency;
  const original = hasDistinctOriginal
    ? formatFiat(payment.originalAmount, payment.originalCurrency, i18n.language)
    : null;
  const settlement = payment.settlement;
  const waivedReason = settlement?.waivedReason ?? null;
  const explorerUrl = safeHttpUrl(payment.explorerTxUrl);
  const platformShare =
    settlement?.platformBase !== null && settlement?.platformBase !== undefined
      ? formatBaseAmount(settlement.platformBase, payment.amountDecimals)
      : null;

  return (
    <TableRow>
      <TableCell className="align-top">
        <div className="text-sm">{payment.storeName ?? t('payments.unknownStore')}</div>
      </TableCell>

      <TableCell className="align-top">
        <CopyableValue
          value={payment.orderId}
          display={truncateMiddle(payment.orderId, 10, 4)}
          label={t('payments.columns.order')}
          className="text-xs"
        />
      </TableCell>

      <TableCell className="whitespace-nowrap align-top">
        {amount ? (
          <span className="font-medium tabular-nums">
            {amount} {payment.asset ?? ''}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('payments.pendingAmount')}</span>
        )}
        {payment.chainId !== null && (
          <div className="text-muted-foreground text-xs">
            {t('payments.chainId', { chainId: payment.chainId })}
          </div>
        )}
      </TableCell>

      <TableCell className="whitespace-nowrap align-top">
        <div className="tabular-nums">{fiat ?? <span className="text-muted-foreground">—</span>}</div>
        {original && <div className="text-muted-foreground text-xs tabular-nums">{original}</div>}
      </TableCell>

      <TableCell className="align-top">
        <div className="flex flex-wrap gap-1">
          <Badge variant={STATUS_VARIANT[payment.status]}>{t(`payments.statuses.${payment.status}`)}</Badge>
          {payment.duplicate && <Badge variant="destructive">{t('payments.duplicateBadge')}</Badge>}
          {payment.test && <Badge variant="blue">{t('payments.testBadge')}</Badge>}
          {payment.rejected && <Badge variant="destructive">{t('payments.orderNotPaidBadge')}</Badge>}
        </div>
        {payment.status === 'underpaid' && (
          <div className="text-muted-foreground mt-1 max-w-[20ch] whitespace-normal text-xs">
            {t('payments.underpaidHint')}
          </div>
        )}
      </TableCell>

      <TableCell className="whitespace-nowrap align-top">
        {!settlement ? (
          <span className="text-muted-foreground text-xs">{t('payments.settlementPending')}</span>
        ) : waivedReason ? (
          <Badge variant="outline">{t('payments.settlementWaived')}</Badge>
        ) : (
          <div className="space-y-0.5">
            <div className="tabular-nums">
              {platformShare ?? '—'} {payment.asset ?? ''}
            </div>
            {settlement.platformBps !== null && (
              <div className="text-muted-foreground text-xs">
                {formatBps(settlement.platformBps, i18n.language)}
              </div>
            )}
          </div>
        )}
      </TableCell>

      <TableCell className="align-top">
        {waivedReason ? (
          <span className="break-all font-mono text-xs">{waivedReason}</span>
        ) : (
          <span className="text-muted-foreground text-xs">{t('payments.notWaived')}</span>
        )}
      </TableCell>

      <TableCell className="align-top">
        {payment.txHash ? (
          <div className="flex items-center gap-1">
            <CopyableValue
              value={payment.txHash}
              display={truncateMiddle(payment.txHash)}
              label={t('payments.columns.txHash')}
              className="text-xs"
            />
            {explorerUrl && (
              <a
                href={explorerUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('payments.viewOnExplorer')}
                className="text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">{t('payments.noTxHash')}</span>
        )}
      </TableCell>

      <TableCell className="text-muted-foreground whitespace-nowrap align-top text-xs">
        {formatDate(payment.createdAt)}
      </TableCell>

      <TableCell className="text-right align-top">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isResyncing}
          onClick={() => onResync(payment.id)}
        >
          <RefreshCw className={isResyncing ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
          {isResyncing ? t('payments.resyncing') : t('payments.resync')}
        </Button>
      </TableCell>
    </TableRow>
  );
}
